import React, { useMemo, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { DemoCTAButtons, PageTitle } from 'Components';
import { Button, Checkbox, Link, TextInput, Password, RadioGroup } from '@Components/common';
import { msgFieldRequired } from '@Helpers/errorMessages';
import { UserTypeEnum } from 'Types/User';
import { RoutingPaths } from '@App/paths';

import styles from './SignUp.module.scss';

export interface SignUpProps {
  onSubmit: (e?: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => Promise<void>;
  register: UseFormMethods['register'];
  fieldErrors: any;
  userType: UserTypeEnum | undefined;
  isLoading: boolean;
  buttonsEnabled: boolean;
}

export const SignUp: React.FC<SignUpProps> = ({
  onSubmit,
  register,
  fieldErrors,
  userType,
  isLoading,
  buttonsEnabled,
}) => {
  const { t } = useTranslation('createAccount');

  const [selectedUserType, setSelectedUserType] = useState(UserTypeEnum.GROUP);
  const userTypeItems = useMemo(
    () => [
      {
        label: (
          <Trans
            ns="createAccount"
            i18nKey="signing.account_type.funder"
            components={[
              <h3 className={styles.cardTitle} />,
              <p className={styles.cardSubTitle} />,
            ]}
          />
        ),
        value: UserTypeEnum.FUNDER,
      },
      {
        label: (
          <Trans
            ns="createAccount"
            i18nKey="signing.account_type.non_profit"
            components={[
              <h3 className={styles.cardTitle} />,
              <p className={styles.cardSubTitle} />,
            ]}
          />
        ),
        value: UserTypeEnum.GROUP,
      },
    ],
    [],
  );
  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <PageTitle title={<Trans ns="createAccount" i18nKey="signing.signup_current_type" />} />
        <p className={styles.subTitle}>{t('signing.signup_subtitle')}</p>

        <form className={styles.inputWrapper} onSubmit={onSubmit}>
          <RadioGroup
            required
            items={userTypeItems}
            name="user_type"
            row
            defaultValue={userType || UserTypeEnum.GROUP}
            card
            setSelectedValue={setSelectedUserType}
          />
          {selectedUserType === UserTypeEnum.GROUP ? (
            <>
              <TextInput
                label={t('signing.org_name')}
                inputRef={register({ required: msgFieldRequired })}
                name="organisation_name"
                error={!!fieldErrors.organisation_name}
                helperText={fieldErrors.organisation_name?.message}
                disabled={isLoading}
              />
              <TextInput
                label={t('signing.contact_name')}
                inputRef={register({ required: msgFieldRequired })}
                name="name"
                error={!!fieldErrors.name}
                helperText={fieldErrors.name?.message}
                disabled={isLoading}
              />
              <TextInput
                label={t('signing.email_address')}
                type="email"
                inputRef={register({ required: msgFieldRequired })}
                name="email"
                error={!!fieldErrors.email}
                helperText={fieldErrors.email?.message}
                disabled={isLoading}
              />
              <Password
                label={t('signing.password')}
                inputRef={register({ required: msgFieldRequired })}
                name="password"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password?.message}
                disabled={isLoading}
                showRules
              />
              <Checkbox
                name="tos_agreement"
                inputRef={register({ required: msgFieldRequired })}
                error={!!fieldErrors.tos_agreement}
                helperText={fieldErrors.tos_agreement?.message}
                disabled={isLoading}
              >
                <Trans
                  ns="createAccount"
                  i18nKey="signing.tos_agreement"
                  components={[
                    <Link href={RoutingPaths.TERMS_OF_USE} primary className={styles.link} />,
                    <Link href={RoutingPaths.PRIVACY_POLICY} primary className={styles.link} />,
                    <Link href={RoutingPaths.COOKIE_POLICY} primary className={styles.link} />,
                  ]}
                />
              </Checkbox>
              <button type="submit" className={styles.hiddenButton} />
              <Button
                buttonType="primary"
                name="sign-up-submit"
                onClick={onSubmit}
                disabled={isLoading || !buttonsEnabled}
                type="submit"
                className={styles.btn}
              >
                {t('signing.sign_up_text')}
              </Button>
            </>
          ) : (
            <div className={styles.funderFormContainer}>
              <h2>{t('signing.funder_form.title')}</h2>
              <p>{t('signing.funder_form.sub_title')}</p>
              <DemoCTAButtons />
            </div>
          )}
        </form>
      </div>
      <p className={styles.subText}>
        <Trans
          ns="createAccount"
          i18nKey="signing.have_account"
          components={[<Link to={RoutingPaths.SIGN_IN} primary className={styles.link} />]}
        />
      </p>
      <p className={styles.rights}>{t('common:copyrights')}</p>
    </div>
  );
};
